/**
 * Справочники: что есть в базе и как этим управлять.
 *
 * Правила этой области — те же, что у карточек, и по той же причине.
 *
 * 1. Скрытие подписано как «из выбора». Скрытый объект остаётся в расчётах, и
 *    если этого не сказать, человек решит, что убрал его из чисел.
 * 2. Роль человека НЕ показывается одним значением. У 76 персон из 263 их
 *    сразу несколько (у Асланяна 155 выпусков ведущим и 154 автором), поэтому
 *    показывается разбивка, а меняется роль в разрезе одной программы.
 * 3. Выпуски в базе и в плейлисте — два столбца, а не один, и разница между
 *    ними подписана: в базе лежат выпуски с отсечки по дате публикации, в
 *    плейлисте — весь архив. Иначе разрыв читается как пробел синхронизации.
 * 4. После смены ведущего рядом появляется требование переразметить. Без него
 *    правка выглядит применённой, а числа остаются прежними.
 */

import { useEffect, useMemo, useState } from "react";

import {
  fetchReference,
  mergePerson,
  patchPerson,
  patchProgram,
  reextractProgram,
  setPersonRole,
} from "../api/client";
import type {
  Reference as ReferenceData,
  ReferencePerson,
  ReferenceProgram,
  ReextractResult,
} from "../api/types";

const ROLES: { key: string; label: string }[] = [
  { key: "host", label: "ведущий" },
  { key: "author", label: "автор" },
  { key: "guest", label: "гость" },
];

export function Reference({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<ReferenceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"programs" | "people">("programs");

  const reload = () =>
    fetchReference()
      .then(setData)
      .catch(() => setError("Справочники не загрузились."));

  useEffect(() => {
    reload();
  }, []);

  /* Строка обновляется из ответа сервера, а не перезапросом всего справочника.
   *
   * Дело не в экономии запроса. Галка «скрыт» — управляемый чекбокс: пока идёт
   * перезагрузка, React перерисовывает строку со старым значением, и галка
   * визуально отскакивает назад. Клик выглядит несработавшим, хотя PATCH
   * прошёл. Ответ каждой правки содержит обновлённую строку — ею и заменяем. */
  const applyProgram = (program: ReferenceProgram) =>
    setData((prev) =>
      prev
        ? { ...prev, programs: prev.programs.map((p) => (p.id === program.id ? program : p)) }
        : prev,
    );

  const applyPerson = (person: ReferencePerson) =>
    setData((prev) =>
      prev
        ? { ...prev, people: prev.people.map((p) => (p.id === person.id ? person : p)) }
        : prev,
    );

  if (error) return <div className="reference"><p className="error">{error}</p></div>;
  if (!data) return <div className="reference"><p className="muted">Загрузка справочников…</p></div>;

  const needHost = data.programs.filter((p) => p.needs_host).length;
  const emptyInBase = data.programs.filter((p) => p.episodes_in_base === 0).length;

  return (
    <div className="reference">
      <header className="reference__head">
        <h2>Справочники</h2>
        <button type="button" className="button button--ghost" onClick={onClose}>
          К дашборду
        </button>
      </header>

      {/* Скрытие — единственная операция, чей смысл легко понять неверно. */}
      <p className="reference__note">
        «Скрыть» убирает объект из списков выбора и из карточки «Люди». На расчёты это не
        влияет: выбранный явно или названный в реплике, он считается как обычно.
      </p>

      <div className="reference__tabs">
        <button
          type="button"
          className={tab === "programs" ? "button" : "button button--ghost"}
          onClick={() => setTab("programs")}
        >
          Программы ({data.programs.length})
        </button>
        <button
          type="button"
          className={tab === "people" ? "button" : "button button--ghost"}
          onClick={() => setTab("people")}
        >
          Люди ({data.people.length})
        </button>
      </div>

      {tab === "programs" ? (
        <>
          <p className="muted">
            Без постоянного ведущего: {needHost}. Без выпусков в базе: {emptyInBase}.
          </p>
          {/* Два столбца со счётчиками рядом приглашают прочесть разницу как
              «не синхронизировано». Это не так, и сказать надо здесь: в базе
              лежат выпуски начиная с отсечки по дате публикации, а в плейлисте
              на YouTube — весь архив программы. */}
          <p className="reference__note">
            «В базе» — выпуски начиная с отсечки по дате публикации
            (<code>YOUTUBE_PUBLISHED_AFTER</code>, сейчас 2025-01-01). «В плейлисте» — весь
            архив программы на YouTube. Разница — это выпуски до отсечки, а не пробел
            синхронизации: ноль в базе при непустом плейлисте значит «свежих выпусков нет».
          </p>
          <ProgramTable programs={data.programs} people={data.people} onApply={applyProgram} />
        </>
      ) : (
        <PeopleTable
          people={data.people}
          programs={data.programs}
          onApply={applyPerson}
          onReload={reload}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ программы

function ProgramTable({
  programs,
  people,
  onApply,
}: {
  programs: ReferenceProgram[];
  people: ReferencePerson[];
  onApply: (program: ReferenceProgram) => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const [failed, setFailed] = useState<number | null>(null);
  const [needsReextract, setNeedsReextract] = useState<Set<number>>(new Set());
  const [report, setReport] = useState<{ id: number; result: ReextractResult } | null>(null);
  const sortedPeople = useMemo(
    () => [...people].sort((a, b) => a.name.localeCompare(b.name, "ru")),
    [people],
  );

  const run = async (id: number, action: () => Promise<void>) => {
    setBusy(id);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="reference__scroll">
      <table className="reference__table">
        <thead>
          <tr>
            <th>Программа</th>
            <th>Тип</th>
            <th className="num">В базе</th>
            <th className="num">В плейлисте, весь архив</th>
            <th>Постоянный ведущий</th>
            <th>Скрыта</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {programs.map((program) => (
            <tr key={program.id} className={program.is_hidden ? "row--hidden" : undefined}>
              <td>
                {program.title}
                {program.is_aggregate && <span className="chip chip--muted">сборная</span>}
              </td>
              <td className="muted">{program.kind}</td>
              <td className="num">{program.episodes_in_base}</td>
              <td className="num">{program.episodes_in_playlist}</td>
              <td>
                <select
                  className="input input--compact"
                  value={program.permanent_host_id ?? ""}
                  disabled={busy === program.id}
                  onChange={(event) => {
                    const value = event.target.value;
                    setNeedsReextract((prev) => new Set(prev).add(program.id));
                    run(program.id, async () => {
                      const done = await patchProgram(program.id, {
                        permanent_host_id: value ? Number(value) : null,
                      });
                      onApply(done.program);
                    });
                  }}
                >
                  <option value="">{program.needs_host ? "не назначен" : "нет"}</option>
                  {sortedPeople.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                    </option>
                  ))}
                </select>
                {program.co_hosts.length > 0 && (
                  <span className="muted"> + {program.co_hosts.map((p) => p.name).join(", ")}</span>
                )}
              </td>
              <td>
                {/* Галка отзывается сразу, до ответа сервера. Иначе React
                    перерисовывает строку прежним значением, и клик выглядит
                    проигнорированным — при живом запросе, который дойдёт.
                    Ошибка возвращает галку назад и говорит об этом. */}
                <input
                  type="checkbox"
                  checked={program.is_hidden}
                  onChange={(event) => {
                    const next = event.target.checked;
                    onApply({ ...program, is_hidden: next });
                    run(program.id, async () => {
                      try {
                        const done = await patchProgram(program.id, { is_hidden: next });
                        onApply(done.program);
                      } catch {
                        onApply({ ...program, is_hidden: !next });
                        setFailed(program.id);
                      }
                    });
                  }}
                />
                {failed === program.id && <span className="error">не сохранилось</span>}
              </td>
              <td>
                {/* Пока не переразметили, назначение ведущего ни на что не
                    влияет. Требование стоит рядом со строкой, а не в общем
                    углу: иначе непонятно, какую программу оно касается. */}
                {needsReextract.has(program.id) && (
                  <button
                    type="button"
                    className="button"
                    disabled={busy === program.id}
                    onClick={() =>
                      run(program.id, async () => {
                        const result = await reextractProgram(program.id);
                        setReport({ id: program.id, result });
                        setNeedsReextract((prev) => {
                          const next = new Set(prev);
                          next.delete(program.id);
                          return next;
                        });
                      })
                    }
                  >
                    {busy === program.id ? "Размечаю…" : "Переразметить"}
                  </button>
                )}
                {report?.id === program.id && <ReextractReport result={report.result} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReextractReport({ result }: { result: ReextractResult }) {
  const delta = result.links_after - result.links_before;
  return (
    <div className="reference__report">
      <p className="muted">
        Выпусков: {result.videos}. Привязок: {result.links_before} → {result.links_after} (
        {delta >= 0 ? `+${delta}` : delta}).
      </p>
      {result.added.length > 0 && (
        <p>
          прибавилось: {result.added.map((r) => `${r.name} (${r.role}) +${r.count}`).join(", ")}
        </p>
      )}
      {result.removed.length > 0 && (
        <p>снялось: {result.removed.map((r) => `${r.name} (${r.role}) −${r.count}`).join(", ")}</p>
      )}
      {result.added.length === 0 && result.removed.length === 0 && (
        <p className="muted">Разметка не изменилась.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- люди

function PeopleTable({
  people,
  programs,
  onApply,
  onReload,
}: {
  people: ReferencePerson[];
  programs: ReferenceProgram[];
  onApply: (person: ReferencePerson) => void;
  onReload: () => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [acting, setActing] = useState<{ id: number; kind: "merge" | "role" } | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = needle
      ? people.filter((person) => person.name.toLowerCase().includes(needle))
      : people;
    return [...list].sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }, [people, query]);

  const run = async (id: number, action: () => Promise<void>) => {
    setBusy(id);
    try {
      await action();
    } finally {
      setBusy(null);
      setActing(null);
    }
  };

  return (
    <>
      <input
        className="input"
        placeholder="поиск по имени"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {note && <p className="reference__note">{note}</p>}
      <div className="reference__scroll">
        <table className="reference__table">
          <thead>
            <tr>
              <th>Человек</th>
              <th className="num">Ведущий</th>
              <th className="num">Автор</th>
              <th className="num">Гость</th>
              <th className="num">Алиасов</th>
              <th>Скрыт</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map((person) => (
              <tr key={person.id} className={person.is_hidden ? "row--hidden" : undefined}>
                <td>
                  {person.name}
                  {!person.first_name && <span className="chip chip--muted">без имени</span>}
                  {person.hosted_programs.length > 0 && (
                    <span className="muted"> ведёт: {person.hosted_programs.join(", ")}</span>
                  )}
                </td>
                {/* Три столбца, а не один «роль»: одно значение соврало бы про
                    каждого, кто был и ведущим, и гостем. */}
                <td className="num">{person.host || ""}</td>
                <td className="num">{person.author || ""}</td>
                <td className="num">{person.guest || ""}</td>
                <td className="num">{person.aliases || ""}</td>
                <td>
                  {/* Оптимистично, как у программ, и по той же причине. */}
                  <input
                    type="checkbox"
                    checked={person.is_hidden}
                    onChange={(event) => {
                      const next = event.target.checked;
                      onApply({ ...person, is_hidden: next });
                      run(person.id, async () => {
                        try {
                          const done = await patchPerson(person.id, { is_hidden: next });
                          onApply(done.person);
                        } catch {
                          onApply({ ...person, is_hidden: !next });
                          setNote("Скрытие не сохранилось — галка возвращена.");
                        }
                      });
                    }}
                  />
                </td>
                <td className="reference__actions">
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() =>
                      setActing(
                        acting?.id === person.id && acting.kind === "role"
                          ? null
                          : { id: person.id, kind: "role" },
                      )
                    }
                  >
                    Роль
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() =>
                      setActing(
                        acting?.id === person.id && acting.kind === "merge"
                          ? null
                          : { id: person.id, kind: "merge" },
                      )
                    }
                  >
                    Слить
                  </button>

                  {acting?.id === person.id && acting.kind === "role" && (
                    <RoleForm
                      programs={programs}
                      busy={busy === person.id}
                      onSubmit={(programId, role) =>
                        run(person.id, async () => {
                          const done = await setPersonRole(person.id, programId, role);
                          onApply(done.person);
                          setNote(
                            `Роль изменена у ${done.changed} привязок, дублей удалено ` +
                              `${done.dropped}. Правка помечена подтверждённой человеком, ` +
                              "переразметка её не откатит.",
                          );
                        })
                      }
                    />
                  )}

                  {acting?.id === person.id && acting.kind === "merge" && (
                    <MergeForm
                      people={people.filter((candidate) => candidate.id !== person.id)}
                      busy={busy === person.id}
                      onSubmit={(targetId) =>
                        run(person.id, async () => {
                          const done = await mergePerson(person.id, targetId);
                          // Слияние удаляет запись — здесь без полной
                          // перезагрузки не обойтись, строки исчезают.
                          onReload();
                          setNote(
                            `Слито: перенесено ${done.moved} привязок, дублей удалено ` +
                              `${done.dropped}. Запись «${person.name}» удалена, её алиасы ` +
                              "теперь ведут к цели.",
                          );
                        })
                      }
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RoleForm({
  programs,
  busy,
  onSubmit,
}: {
  programs: ReferenceProgram[];
  busy: boolean;
  onSubmit: (programId: number, role: string) => void;
}) {
  const [programId, setProgramId] = useState("");
  const [role, setRole] = useState("host");
  const sorted = useMemo(
    () => [...programs].sort((a, b) => a.title.localeCompare(b.title, "ru")),
    [programs],
  );

  return (
    <div className="reference__form">
      {/* Роль меняется в разрезе программы: глобальной роли у человека нет. */}
      <p className="muted">Во всех выпусках программы:</p>
      <select
        className="input input--compact"
        value={programId}
        onChange={(event) => setProgramId(event.target.value)}
      >
        <option value="">выберите программу</option>
        {sorted.map((program) => (
          <option key={program.id} value={program.id}>
            {program.title}
          </option>
        ))}
      </select>
      <select
        className="input input--compact"
        value={role}
        onChange={(event) => setRole(event.target.value)}
      >
        {ROLES.map((item) => (
          <option key={item.key} value={item.key}>
            {item.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="button"
        disabled={busy || !programId}
        onClick={() => onSubmit(Number(programId), role)}
      >
        {busy ? "Меняю…" : "Применить"}
      </button>
    </div>
  );
}

function MergeForm({
  people,
  busy,
  onSubmit,
}: {
  people: ReferencePerson[];
  busy: boolean;
  onSubmit: (targetId: number) => void;
}) {
  const [targetId, setTargetId] = useState("");
  const sorted = useMemo(
    () => [...people].sort((a, b) => a.name.localeCompare(b.name, "ru")),
    [people],
  );

  return (
    <div className="reference__form">
      {/* Необратимо, поэтому сказано прямо, а не мелким шрифтом. */}
      <p className="muted">Слить в другого человека. Эта запись будет удалена.</p>
      <select
        className="input input--compact"
        value={targetId}
        onChange={(event) => setTargetId(event.target.value)}
      >
        <option value="">выберите, в кого слить</option>
        {sorted.map((person) => (
          <option key={person.id} value={person.id}>
            {person.name} ({person.host + person.author + person.guest})
          </option>
        ))}
      </select>
      <button
        type="button"
        className="button"
        disabled={busy || !targetId}
        onClick={() => onSubmit(Number(targetId))}
      >
        {busy ? "Сливаю…" : "Слить"}
      </button>
    </div>
  );
}
